import { Clock, Effect } from 'effect';
import {
  ClientHttpError,
  readClientResponseTextEffect,
  withFetchedResponseEffect,
} from '../client';
import type { NextAction } from './output';

export type DoctorCheckStatus = 'ok' | 'warn' | 'fail' | 'unknown';

export interface DoctorCheck {
  id: 'node' | 'config' | 'auth' | 'api_reachability' | 'cli_version' | 'self_host_env';
  status: DoctorCheckStatus;
  title: string;
  detail: string;
  data: Record<string, unknown>;
  next_actions: NextAction[];
}

/**
 * Compare two semver-ish versions. Returns -1 if a<b, 0 if equal, 1 if a>b.
 * Compares MAJOR.MINOR.PATCH numerically; a prerelease (x.y.z-foo) sorts BEFORE
 * its release (x.y.z) per semver. Used so `okra doctor` doesn't call a NEWER
 * local build "behind" an older npm `latest` (a bare `installed === latest`
 * mislabels any difference — including ahead — as behind).
 */
export function compareSemver(a: string, b: string): -1 | 0 | 1 {
  const parse = (v: string) => {
    const [core, pre] = v.trim().replace(/^v/, '').split('-', 2);
    const nums = core.split('.').map((n) => Number.parseInt(n, 10) || 0);
    return { nums, pre: pre ?? null };
  };
  const pa = parse(a);
  const pb = parse(b);
  for (let i = 0; i < Math.max(pa.nums.length, pb.nums.length); i += 1) {
    const d = (pa.nums[i] ?? 0) - (pb.nums[i] ?? 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  if (pa.pre === pb.pre) return 0;
  if (pa.pre === null) return 1; // a is a release, b is a prerelease → a > b
  if (pb.pre === null) return -1;
  return pa.pre < pb.pre ? -1 : 1;
}

const timedOut = (method: string, timeoutMs: number) =>
  new ClientHttpError({ method, cause: new Error(`Request timed out after ${timeoutMs}ms`) });

export function checkCliVersionEffect(
  installed: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Effect.Effect<DoctorCheck> {
  return Effect.andThen(
    Effect.yieldNow,
    withFetchedResponseEffect(
      { method: 'GET', headers: { Accept: 'application/json' } },
      (init) => fetchImpl('https://registry.npmjs.org/@okrapdf/cli/latest', init),
      (response) =>
        Effect.gen(function* (): Effect.fn.Return<DoctorCheck, ClientHttpError> {
          if (!response.ok) {
            return {
              id: 'cli_version',
              status: 'unknown',
              title: 'CLI version',
              detail: `Could not read latest @okrapdf/cli version from npm (${response.status}).`,
              data: { installed, latest: null, up_to_date: null, status: response.status },
              next_actions: [],
            };
          }
          const text = yield* readClientResponseTextEffect(response);
          const payload = yield* Effect.try({
            try: () => JSON.parse(text) as Record<string, unknown>,
            catch: (cause) => new ClientHttpError({ method: 'doctor_version', cause }),
          });
          const latest = typeof payload?.version === 'string' ? payload.version : null;
          if (!latest) {
            return {
              id: 'cli_version',
              status: 'unknown',
              title: 'CLI version',
              detail: 'npm returned an unexpected latest-version response.',
              data: { installed, latest: null, up_to_date: null },
              next_actions: [],
            };
          }
          // Order-aware: only an installed version that is strictly OLDER than npm
          // `latest` is "behind" (warn + upgrade). Equal is current; NEWER (a dev/
          // unpublished build, e.g. local 0.16.2 vs npm 0.16.1) is ahead — not a
          // problem, so don't warn or prompt an "upgrade" that would downgrade.
          const cmp = compareSemver(installed, latest);
          const behind = cmp < 0;
          const ahead = cmp > 0;
          return {
            id: 'cli_version',
            status: behind ? 'warn' : 'ok',
            title: 'CLI version',
            detail: behind
              ? `Installed CLI version ${installed} is behind latest ${latest}.`
              : ahead
                ? `Installed CLI version ${installed} is ahead of the latest published ${latest} (dev build).`
                : `Installed CLI version ${installed} is current.`,
            data: { installed, latest, up_to_date: cmp === 0, ahead },
            next_actions: behind
              ? [
                  {
                    cmd: 'npm install -g @okrapdf/cli@latest',
                    why: 'Upgrade to the latest published CLI.',
                  },
                ]
              : [],
          };
        }),
    ),
  ).pipe(
    Effect.timeoutOrElse({
      duration: 2000,
      orElse: () => Effect.fail(timedOut('doctor_version', 2000)),
    }),
    Effect.catch((error) =>
      Effect.sync((): DoctorCheck => {
        return {
          id: 'cli_version',
          status: 'unknown',
          title: 'CLI version',
          detail: `Could not check npm for the latest CLI version: ${error.cause instanceof Error ? error.cause.message : String(error.cause)}`,
          data: { installed, latest: null, up_to_date: null },
          next_actions: [],
        };
      }),
    ),
  );
}

export const checkApiReachabilityEffect = Effect.fn('cli.doctor.health')(function* (
  baseUrl: string,
  baseUrlError?: string,
  fetchImpl: typeof fetch = globalThis.fetch,
): Effect.fn.Return<DoctorCheck> {
  yield* Effect.yieldNow;
  if (baseUrlError) {
    return {
      id: 'api_reachability',
      status: 'fail',
      title: 'API reachability',
      detail: `Cannot check /health because the base URL is invalid: ${baseUrlError}`,
      data: { base_url: baseUrl, status: null, latency_ms: null },
      next_actions: [
        {
          cmd: 'okra doctor --base-url https://api.okrapdf.com',
          why: 'Run diagnostics against a valid HTTP(S) API endpoint.',
        },
      ],
    };
  }

  const healthUrl = `${baseUrl}/health`;
  const startedAt = yield* Clock.currentTimeMillis;
  return yield* withFetchedResponseEffect(
    { method: 'GET' },
    (init) => fetchImpl(healthUrl, init),
    (response) =>
      Effect.gen(function* (): Effect.fn.Return<DoctorCheck> {
        const latencyMs = (yield* Clock.currentTimeMillis) - startedAt;
        return {
          id: 'api_reachability',
          status: response.ok ? 'ok' : 'fail',
          title: 'API reachability',
          detail: response.ok
            ? `Reached ${healthUrl} in ${latencyMs}ms.`
            : `${healthUrl} returned HTTP ${response.status}.`,
          data: { url: healthUrl, status: response.status, latency_ms: latencyMs },
          next_actions: response.ok
            ? []
            : [
                {
                  cmd: `okra doctor --base-url ${shellQuote(baseUrl)}`,
                  why: 'Re-run diagnostics after checking the API health endpoint.',
                },
              ],
        };
      }),
  ).pipe(
    Effect.timeoutOrElse({
      duration: 3000,
      orElse: () => Effect.fail(timedOut('doctor_health', 3000)),
    }),
    Effect.catch((error) =>
      Effect.gen(function* (): Effect.fn.Return<DoctorCheck> {
        const latencyMs = (yield* Clock.currentTimeMillis) - startedAt;
        return {
          id: 'api_reachability',
          status: 'fail',
          title: 'API reachability',
          detail: `Could not reach ${healthUrl}: ${error.cause instanceof Error ? error.cause.message : String(error.cause)}`,
          data: { url: healthUrl, status: null, latency_ms: latencyMs },
          next_actions: [
            {
              cmd: `okra doctor --base-url ${shellQuote(baseUrl)}`,
              why: 'Verify the configured API host and network path.',
            },
          ],
        };
      }),
    ),
  );
});

function shellQuote(value: string): string {
  return /^[A-Za-z0-9._:/=@-]+$/.test(value) ? value : JSON.stringify(value);
}
